<template>
  <v-dialog
    :model-value="open"
    @update:model-value="close"
    fullscreen
    transition="dialog-bottom-transition"
    :aria-label="`Cooking ${title}`"
  >
    <v-card class="d-flex flex-column" @keydown="onKey">
      <v-toolbar color="surface">
        <v-btn icon="mdi-close" aria-label="Exit cooking mode" @click="close" />
        <v-toolbar-title>{{ title }}</v-toolbar-title>
      </v-toolbar>
      <v-progress-linear
        :model-value="progress"
        aria-label="Steps done"
        color="primary"
        height="6"
      />

      <v-expansion-panels class="px-4 pt-4">
        <v-expansion-panel :title="`Ingredients (${ingredients.length})`">
          <v-expansion-panel-text>
            <v-list density="compact">
              <v-list-item
                v-for="(ingredient, index) in ingredients"
                :key="index"
                :title="ingredient.text"
                :subtitle="ingredient.amount"
              />
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-card-text v-if="steps.length" class="flex-grow-1">
        <p class="text-label-large">
          Step {{ current + 1 }} of {{ steps.length }}
        </p>
        <p class="text-headline-small step" aria-live="polite">
          {{ steps[current].text }}
        </p>
        <v-checkbox
          :model-value="done.has(current)"
          @update:model-value="toggleDone"
          label="Done"
          hide-details
        />
      </v-card-text>
      <v-card-text v-else class="flex-grow-1">
        This recipe has no steps yet.
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn
          size="large"
          prepend-icon="mdi-chevron-left"
          :disabled="current === 0"
          @click="go(-1)"
        >
          Previous
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="current < steps.length - 1"
          size="large"
          color="primary"
          variant="flat"
          append-icon="mdi-chevron-right"
          @click="next"
        >
          Next
        </v-btn>
        <v-btn
          v-else
          size="large"
          color="primary"
          variant="flat"
          prepend-icon="mdi-check"
          @click="finish"
        >
          Finish
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import {
    computed,
    defineComponent,
    onBeforeUnmount,
    type PropType,
    ref,
    watch,
  } from 'vue';
  import type { Step } from '@/service/recipe/types';
  import { createWakeLock, type WakeLock } from '@/service/cooking/wakeLock';

  export interface CookingIngredient {
    text: string;
    // Already scaled and formatted, e.g. "1 ½ Cup(s)"
    amount: string;
  }

  export default defineComponent({
    name: 'CookingMode',
    props: {
      open: {
        type: Boolean,
        default: false,
      },
      title: {
        type: String,
        required: true,
      },
      steps: {
        type: Array as PropType<Step[]>,
        default: () => [],
      },
      ingredients: {
        type: Array as PropType<CookingIngredient[]>,
        default: () => [],
      },
      wakeLock: {
        type: Object as PropType<WakeLock>,
        default: () => createWakeLock(),
      },
    },
    emits: {
      close: () => true,
    },
    setup(props, { emit }) {
      const current = ref(0);
      // Ticked steps last while the recipe page is open, not beyond
      const done = ref(new Set<number>());

      watch(
        () => props.open,
        async (open) => {
          if (open) {
            await props.wakeLock.acquire();
          } else {
            await props.wakeLock.release();
          }
        },
        { immediate: true },
      );
      onBeforeUnmount(() => props.wakeLock.release());

      function go(by: number) {
        current.value = Math.min(
          Math.max(current.value + by, 0),
          Math.max(props.steps.length - 1, 0),
        );
      }

      function markDone(index: number, isDone: boolean) {
        const next = new Set(done.value);
        if (isDone) next.add(index);
        else next.delete(index);
        done.value = next;
      }

      function close() {
        emit('close');
      }

      return {
        current,
        done,
        progress: computed(() =>
          props.steps.length ? (done.value.size / props.steps.length) * 100 : 0,
        ),
        go,
        close,
        toggleDone(isDone: boolean | null) {
          markDone(current.value, !!isDone);
        },
        // Moving on ticks the step off
        next() {
          markDone(current.value, true);
          go(1);
        },
        finish() {
          if (props.steps.length) markDone(current.value, true);
          close();
        },
        onKey(event: KeyboardEvent) {
          if (event.key === 'ArrowRight') go(1);
          if (event.key === 'ArrowLeft') go(-1);
        },
      };
    },
  });
</script>

<style scoped>
  .step {
    line-height: 1.5;
    white-space: pre-line;
  }
</style>
